import { Injectable } from '@angular/core';
import { shareReplay } from 'rxjs/operators';

import * as _ from 'lodash';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';

import {
    AppState,
    BackButtonState,
    Extra,
    KeyValueMap,
    Lang,
    Option,
    OptionsMap,
    PageStateEnum,
    TokenData,
    UserRole,
    initialState
} from '@appCore/models';
import { Permission } from '@appCore/models/role';
import { TokenField, getTokenNumericPayloadData, getTokenPayloadData } from '@appCore/utilities';

@Injectable({
    providedIn: 'root'
})
export class StoreService {
    private STORAGE_KEY = 'appState';
    private state = this.getStateFromLocalStorage();
    private stateSource = new BehaviorSubject<AppState>(this.state);
    public state$ = this.stateSource.asObservable();

    public reset() {
        this.state = initialState;
        localStorage.removeItem(this.STORAGE_KEY);
    }

    // Setters

    public setUser(id: string, name: string, role: string, application: string, preferedDepartamentId: number) {
        const state = _.cloneDeep(this.state);
        state.userState = { id, preferedLang: Lang.Spanish, name, role, application, preferedDepartamentId };
        this.state = state;
        this.updateStore();
    }

    public setTokenData(tokenData: TokenData) {
        const state = _.cloneDeep(this.state);
        state.tokenData = tokenData;
        this.state = state;
        this.updateStore();
    }

    public setLang(preferedLang: Lang) {
        const user = { ...this.state.userState, preferedLang };
        this.state = { ...this.state, userState: user };
        this.updateStore();
    }

    public setIsLoading(isLoading: boolean) {
        this.state = { ...this.state, isLoading };
        this.updateStore();
    }

    public setNoticesCount(noticeCount: number) {
        this.state = { ...this.state, consultationState: { ...this.state.consultationState, ...{ noticeCount } } };
        this.updateStore();
    }
    public setContractsCount(contractsCount: number) {
        this.state = { ...this.state, consultationState: { ...this.state.consultationState, ...{ contractsCount } } };
        this.updateStore();
    }

    public setCollaborator(collaboratorId: number) {
        this.state = { ...this.state, customerState: { ...this.state.customerState, ...{ collaboratorId } } };
        this.updateStore();
    }

    public setPersona(personaId: number) {
        this.state = { ...this.state, customerState: { ...this.state.customerState, ...{ personaId } } };
        this.updateStore();
    }

    public setPermission(permission: Permission) {
        this.state = { ...this.state, permission };
        this.updateStore();
    }

    public setPermissions(permissions: Permission[]): void {
        this.state = { ...this.state, permissionData: permissions };
        this.updateStore();
    }

    /** Sets the module option map object */
    public setOptionsMap(stateKeyAccessor: PageStateEnum, optionsMap: KeyValueMap<Option[]>): void {
        // //console.log(`setOptionsMap ${stateKeyAccessor}`, optionsMap);
        this.state = { ...this.state, [stateKeyAccessor]: { ...this.state[stateKeyAccessor], optionsMap } };
        this.updateStore();
    }

    /** Sets the module current id */
    public setCurrentId(stateKeyAccessor: PageStateEnum, currentId: number): void {
        this.state = { ...this.state, [stateKeyAccessor]: { ...this.state[stateKeyAccessor], currentId } };
        this.updateStore();
    }

    /** Sets the module current name */
    public setCurrentName(stateKeyAccessor: PageStateEnum, name: string): void {
        this.state = { ...this.state, [stateKeyAccessor]: { ...this.state[stateKeyAccessor], name } };
        this.updateStore();
    }

    /** Sets the module extra datas */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setExtraData(stateKeyAccessor: PageStateEnum, extra: any): void {
        this.state = { ...this.state, [stateKeyAccessor]: { ...this.state[stateKeyAccessor], extra } };
        this.updateStore();
    }

    /** Sets the module customerId associate to an office */
    public setCurrentCustomerByOffice(stateKeyAccessor: PageStateEnum, customerId: number): void {
        this.state = { ...this.state, [stateKeyAccessor]: { ...this.state[stateKeyAccessor], customerId } };
        this.updateStore();
    }

    public setBackButtonState(backButton: BackButtonState): void {
        this.state = { ...this.state, navigation: { ...this.state.navigation, ...{ backButton } } };
        this.updateStore();
    }

    // Getters

    /** Checks if the user is authenticated with a token */
    public isAuthenticated(): boolean {
        const { accessToken } = this.getTokenData();
        return !!accessToken;
    }

    /** Gets the user role from accessToken */
    public getUserRole(): UserRole {
        // const { accessToken } = this.getTokenData();
        const role = this.state.userState.role;
        return (role as UserRole) || UserRole.NULL;
    }

    /** Gets the user name from accessToken */
    public getUserName(): string {
        const { accessToken } = this.getTokenData();
        return getTokenPayloadData(accessToken, TokenField.UserName);
    }

    public getPersonName(): string {
        const { accessToken } = this.getTokenData();
        const personName = getTokenPayloadData(accessToken, TokenField.PersonName);
        // If PersonName is empty or null, return UserName instead
        if (!personName) {
            return this.getUserName().split('@')[0];
        }
        return personName;
    }

    public getPreferedDepartamentId(): number {
        const { accessToken } = this.getTokenData();
        return getTokenNumericPayloadData(accessToken, TokenField.DepartamentId);
    }

    public getDepartament(): string {
        const { accessToken } = this.getTokenData();
        return getTokenPayloadData(accessToken, TokenField.Department);
    }

    /** Gets the accessToken and refreshToken */
    public getTokenData(): TokenData {
        return this.state.tokenData;
    }

    /** Gets the user prefered language*/
    public getLang(): Lang {
        return this.state.userState.preferedLang;
    }

    /** Gets the module option map object */
    public getOptionsMap(stateKeyAccessor: PageStateEnum): OptionsMap {
        return this.state[stateKeyAccessor].optionsMap;
    }

    /** Gets an option list from the module option map object */
    public getOptions(stateKeyAccessor: PageStateEnum, keyAccessor: string): Option[] {
        return this.state[stateKeyAccessor].optionsMap[keyAccessor];
    }

    public getPermission(): Permission {
        return this.state.permission;
    }

    public getPermissions(): Permission[] {
        return this.state.permissionData;
    }

    /** Gets the module current id */
    public getCurrentId(stateKeyAccessor: PageStateEnum): number {
        return this.state[stateKeyAccessor].currentId;
    }

    public getCollaboratorId(stateKeyAccessor: PageStateEnum): any {
        return this.state[stateKeyAccessor].collaboratorId;
    }

    public getPersonaId(stateKeyAccessor: PageStateEnum): any {
        return this.state[stateKeyAccessor].personaId;
    }

    /** Gets the module current name */
    public getCurrentName(stateKeyAccessor: PageStateEnum): string {
        return this.state[stateKeyAccessor].name;
    }

    /** Gets the module extra data */
    public getExtraData(stateKeyAccessor: PageStateEnum): Extra {
        return this.state[stateKeyAccessor]?.extra ?? {};
    }

    public getCurrentCustomerByOffice(stateKeyAccessor: PageStateEnum): number {
        return this.state[stateKeyAccessor].customerId || 0;
    }

    // Selectors

    public selectIsLoading(): Observable<boolean> {
        return this.state$.pipe(
            map((state) => state.isLoading),
            distinctUntilChanged()
        );
    }

    public selectBackButtonState(): Observable<BackButtonState> {
        return this.state$.pipe(
            map((state) => state.navigation.backButton),
            distinctUntilChanged()
        );
    }

    public selectNoticesCount(): Observable<number> {
        return this.state$.pipe(
            map((state) => state.consultationState.noticeCount),
            distinctUntilChanged()
        );
    }
    public selecContractsCount(): Observable<number> {
        return this.state$.pipe(
            map((state) => state.consultationState.contractsCount),
            distinctUntilChanged()
        );
    }

    public selectPermission(): Observable<Permission> {
        return this.state$.pipe(
            map((state) => state.permission),
            distinctUntilChanged((previous, current) => previous.functionalityName !== current.functionalityName),
            shareReplay()
        );
    }

    public resetCollaborator() {
        const state = _.cloneDeep(this.state);
        state.customerState.collaboratorId = undefined;
        this.state = state;
        this.updateStore();
    }

    public resetPersona() {
        const state = _.cloneDeep(this.state);
        state.customerState.personaId = undefined;
        this.state = state;
        this.updateStore();
    }

    // Private
    private updateStore() {
        this.stateSource.next(this.state);
        this.saveStateToLocalStorage(this.state);
    }

    private saveStateToLocalStorage(appState: AppState): void {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(appState));
    }

    private getStateFromLocalStorage(): AppState {
        const state = localStorage.getItem(this.STORAGE_KEY);
        return state ? this.getParsedState(state) : initialState;
    }

    private getParsedState(state: string): AppState {
        return JSON.parse(state) as AppState;
    }
}
